/**
 * Merge scheduling compute artifacts with persisted generic run artifacts (Level 4a).
 */

import type { AiStudioRun } from '@/types/aiStudioAgent';
import type { RunArtifact, ArtifactBody, ArtifactDecisionMeta } from './artifacts';
import { buildSchedulingArtifacts } from './buildSchedulingArtifacts';
import { asArtifactBlocks } from './rendering/artifactPrimitiveStyles';

export interface PersistedRunArtifactDto {
  id: string;
  title: string;
  summary?: string;
  kind: 'schedule' | 'document' | 'data';
  status?: 'draft' | 'saved';
  body: ArtifactBody | Record<string, unknown>;
  scope?: 'session' | 'store';
  persistence?: 'episode' | 'instance';
  expiresAt?: string | null;
  stale?: boolean;
  savedAt?: string | null;
  createdAt?: string | null;
}

function mapPersistedArtifact(row: PersistedRunArtifactDto): RunArtifact | null {
  const body = row.body;
  if (!body || typeof body !== 'object') return null;
  const raw = body as Record<string, unknown>;

  // Tolerate missing/alias types from older or LLM-shaped payloads
  let typed: ArtifactBody;
  if (
    raw['type'] === 'text' ||
    raw['type'] === 'json' ||
    raw['type'] === 'schedule' ||
    raw['type'] === 'document'
  ) {
    typed = body as ArtifactBody;
  } else if (Array.isArray(raw['blocks']) && raw['blocks'].length > 0) {
    typed = {
      type: 'document',
      title: typeof raw['title'] === 'string' ? raw['title'] : undefined,
      blocks: asArtifactBlocks(raw['blocks']),
      _decision: raw['_decision'] as ArtifactDecisionMeta | undefined,
    };
  } else if (typeof raw['text'] === 'string' && raw['text'].trim()) {
    typed = {
      type: 'text',
      text: raw['text'],
      _decision: raw['_decision'] as ArtifactDecisionMeta | undefined,
    };
  } else if (typeof raw['markdown'] === 'string' && raw['markdown'].trim()) {
    typed = {
      type: 'text',
      text: raw['markdown'],
      _decision: raw['_decision'] as ArtifactDecisionMeta | undefined,
    };
  } else if (typeof raw['content'] === 'string' && raw['content'].trim()) {
    typed = {
      type: 'text',
      text: raw['content'],
      _decision: raw['_decision'] as ArtifactDecisionMeta | undefined,
    };
  } else if (
    raw['data'] !== undefined ||
    Array.isArray(raw['rows']) ||
    Array.isArray(raw['items']) ||
    Array.isArray(raw['columns'])
  ) {
    typed = {
      type: 'json',
      data:
        raw['data'] !== undefined
          ? raw['data']
          : { columns: raw['columns'], rows: raw['rows'] ?? raw['items'] },
      _decision: raw['_decision'] as ArtifactDecisionMeta | undefined,
    };
  } else {
    typed = {
      type: 'json',
      data: raw,
      _decision: raw['_decision'] as ArtifactDecisionMeta | undefined,
    };
  }

  const scope =
    row.scope ??
    (row.persistence === 'instance' ? 'store' : 'session');
  return {
    id: row.id,
    title: row.title,
    summary: row.summary,
    kind: row.kind,
    body: typed,
    status: row.status,
    scope,
    persistence: row.persistence,
    expiresAt: row.expiresAt,
    stale: row.stale,
    savedAt: row.savedAt ?? null,
  };
}

/**
 * Outputs rail shows built artifacts immediately (propose publishes).
 * Persistence (session vs store / TTL) is owned by agent I/O definition — not by
 * a separate confirm-to-show step.
 */
export function buildRunArtifacts(
  run: AiStudioRun,
  persisted: PersistedRunArtifactDto[] = []
): RunArtifact[] {
  const scheduling = buildSchedulingArtifacts(run);
  const generic = [...persisted]
    .filter((p) => p.status === 'saved' || p.status === 'draft')
    .sort((a, b) => {
      const ta = Date.parse(String(a.savedAt ?? a.createdAt ?? 0));
      const tb = Date.parse(String(b.savedAt ?? b.createdAt ?? 0));
      if (tb !== ta) return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
      const storeRank = (x: PersistedRunArtifactDto) =>
        x.scope === 'store' || x.persistence === 'instance' ? 0 : 1;
      return storeRank(a) - storeRank(b);
    })
    .map(mapPersistedArtifact)
    .filter((a): a is RunArtifact => a !== null);
  return [...scheduling, ...generic];
}
